import type { Notification } from "@/hooks/use-notifications";

/**
 * Ruter et varsel til riktig side med deep-link parametre slik at
 * landings-siden kan scrolle og highlighte korrekt entitet.
 * Faller tilbake til dashboard hvis ingen meningsfull destinasjon.
 */
export function routeForNotification(n: Pick<Notification, "type" | "entity_id" | "project_id"> & { entity_type?: string | null }): string {
  const id = n.entity_id;
  // CRM-4.6: møter uten kunde (lead-møter) varsles med møtets id; referatsiden håndterer begge.
  if (n.entity_type === "crm_meetings" && id) return `/moter/${id}`;
  switch (n.type) {
    case "task_pending_approval":
      // PL/produksjonslead lander på godkjenningsfanen i prosjektet
      if (n.project_id && id) return `/prosjekter/${n.project_id}/godkjenne?highlight=${id}`;
      if (n.project_id) return `/prosjekter/${n.project_id}/godkjenne`;
      break;
    case "task_assigned":
    case "task_approved":
    case "task_rejected":
    case "task_comment_added":
      if (id) return `/mine-oppgaver?highlight=${id}`;
      return "/mine-oppgaver";
    case "project_update":
      if (n.project_id && id) return `/prosjekter/${n.project_id}?commentId=${id}`;
      if (n.project_id) return `/prosjekter/${n.project_id}`;
      break;
    case "voucher_pending":
      if (n.project_id && id)
        return `/prosjekter/${n.project_id}/okonomi?seksjon=bilag&voucherId=${id}`;
      if (n.project_id) return `/prosjekter/${n.project_id}/okonomi?seksjon=bilag`;
      break;
    case "timebank_requested":
      if (id) return `/timebank?highlight=${id}`;
      return "/timebank";
    case "timebank_approved":
    case "timebank_rejected":
    case "timebank_cancelled":
      // Lander på Mine oppgaver som har TimebankCard øverst
      if (id) return `/mine-oppgaver?highlight=${id}`;
      return "/mine-oppgaver";
    case "feedback_resolved":
      if (id) return `/mine-tilbakemeldinger?highlight=${id}`;
      return "/mine-tilbakemeldinger";
    case "missing_time_entries":
      // VIB-1296: samme destinasjon som deep-linken i e-postvarselet.
      return "/mine-oppgaver";
    case "offer_expiring":
      // entity_id = offers.id (VIB-1304)
      if (id) return `/tilbud/${id}`;
      return "/tilbud";
    case "crm_reply_received":
    case "crm_signal_claimed":
    case "crm_meeting_booked":
    case "crm_summary_ready":
      // CRM: entity_id = customers.id (kundekortet).
      if (id) return `/kunde/${id}`;
      return "/kunder";
    case "crm_follow_ups_today":
      return "/salgsmuligheter";
    case "equipment_damage":
    case "equipment_booking_alert":
      // VIB-2933: entity_id = equipment_items.id (utstyrskortet).
      if (id) return `/utstyr/${id}`;
      return "/utstyr";
    case "chat_mention":
      // VIB-3025: entity_id = chat_rooms.id, åpner kanalen i Chat-rommet.
      if (id) return `/chat?kanal=${encodeURIComponent(`room:${id}`)}`;
      return "/chat";
    case "freelancer_mention":
      // VIB-2925: entity_id = freelancers.id.
      if (id) return `/frilansere/${id}`;
      return "/frilansere";
    case "absence_registered":
      // VIB-1653: entity_id = absences.id. Slettet fravær beholder id-en i
      // varselet, men raden finnes ikke lenger — Timebank-seksjonen viser da
      // ingen highlight, og lederen ser bare listen uten den.
      if (id) return `/timebank?highlight=${id}`;
      return "/timebank";
  }
  if (n.project_id) return `/prosjekter/${n.project_id}`;
  return "/";
}
